import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionStorageService } from '../session-storage.service';
import { LocalStorageKey } from '../models/storageKey';
import { AnalyticsService } from '../analytics/analytics.service';
import { FunnelTrackingService } from '../analytics/funnel-tracking.service';

interface VenueTypeCard {
  id: string;
  title: string;
  description: string;
  /** Value saved as venueCategoryType. */
  categoryType: string;
  /** Value saved as venueType; null means the user describes it themselves. */
  venueType: string | null;
}

@Component({
  selector: 'app-venue-type',
  templateUrl: './venue-type.component.html',
  styleUrls: ['./venue-type.component.css']
})
export class VenueTypeComponent implements OnInit, OnDestroy {
  cards: VenueTypeCard[] = [
    {
      id: 'school-booster',
      title: 'School / booster concessions',
      description: 'Gym, field or stadium stand run by a club',
      categoryType: 'Concession Stand',
      venueType: 'School/Booster Club'
    },
    {
      id: 'independent-stand',
      title: 'Independent concession stand',
      description: 'Rec league, fairground, community venue',
      categoryType: 'Concession Stand',
      venueType: 'Independent/Community'
    },
    {
      id: 'food-truck',
      title: 'Food truck or trailer',
      description: 'Mobile, location changes',
      categoryType: 'Food Truck',
      venueType: 'Food Truck/Trailer'
    },
    {
      id: 'golf',
      title: 'Golf course or club',
      description: 'Turn stand, cart service, grill',
      categoryType: 'Golf Course',
      venueType: 'Golf Course/Club'
    },
    {
      id: 'stadium',
      title: 'Stadium, arena or college',
      description: 'Multiple stands, staffed operation',
      categoryType: 'Stadium/Arena',
      venueType: 'Stadium/Arena/College'
    },
    {
      id: 'other',
      title: 'Something else',
      description: 'Describe it in one line',
      categoryType: 'Other',
      venueType: null
    }
  ];

  nonprofitOptions: { value: string; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'not_sure', label: 'Not sure' }
  ];

  orderVolumeOptions: string[] = [
    'Under 500',
    '500 - 2,000',
    '2,000 - 10,000',
    'Over 10,000'
  ];

  selectedCardId: string | null = null;
  customDescription = '';
  nonprofitStatus = '';
  monthlyOrderVolume = '';

  private pageStartTime = 0;
  private storageSubscription?: Subscription;

  constructor(
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private analytics: AnalyticsService,
    private funnelTracking: FunnelTrackingService
  ) {}

  ngOnInit(): void {
    // Step views and page timing are handled by FunnelTrackingService;
    // pageStartTime only feeds intra-page interaction timings below.
    this.pageStartTime = Date.now();

    // Load any previously selected values. Reactive because the session doc
    // restore is async; stop applying once the user makes a selection.
    this.storageSubscription = this.sessionStorageService.storage$.subscribe(storage => {
      if (this.selectedCardId) {
        return;
      }
      this.nonprofitStatus = storage[LocalStorageKey.NONPROFIT_STATUS] || this.nonprofitStatus;
      this.monthlyOrderVolume = storage[LocalStorageKey.MONTHLY_ORDER_VOLUME] || this.monthlyOrderVolume;

      const storedCategory = storage[LocalStorageKey.VENUE_CATEGORY_TYPE];
      const storedType = storage[LocalStorageKey.VENUE_TYPE];
      if (!storedCategory) {
        return;
      }
      // Exact card match first; then by category (covers pre-redesign
      // subtypes); anything else restores as a custom description.
      const match =
        this.cards.find(card => card.categoryType === storedCategory && card.venueType === storedType) ||
        this.cards.find(card => card.categoryType === storedCategory && card.venueType !== null);
      if (match) {
        this.selectedCardId = match.id;
      } else {
        this.selectedCardId = 'other';
        this.customDescription = storedType || '';
      }
    });
  }

  ngOnDestroy(): void {
    this.storageSubscription?.unsubscribe();
  }

  get selectedCard(): VenueTypeCard | null {
    return this.cards.find(card => card.id === this.selectedCardId) || null;
  }

  get isOtherSelected(): boolean {
    return this.selectedCardId === 'other';
  }

  onCardSelect(card: VenueTypeCard): void {
    const previousCard = this.selectedCard;
    this.selectedCardId = card.id;

    if (previousCard?.id !== card.id) {
      this.analytics.trackEvent('venue_type_explored', {
        venue_type: card.categoryType,
        venue_subtype: card.venueType,
        previous_venue: previousCard?.categoryType || null,
        time_since_page_load: Date.now() - this.pageStartTime
      });
    }
  }

  onNonprofitSelect(value: string): void {
    this.nonprofitStatus = value;
  }

  isReadyToContinue(): boolean {
    const card = this.selectedCard;
    if (!card) return false;
    if (card.venueType === null) {
      return this.customDescription.trim().length > 0;
    }
    return true;
  }

  onBack(): void {
    this.router.navigate(['/login']);
  }

  onContinue(): void {
    const card = this.selectedCard;
    if (!card || !this.isReadyToContinue()) {
      this.analytics.trackFormError('venue_selection', 'incomplete_selection', 'venue_type',
        'Venue type not selected');
      alert('Please select a venue type before proceeding.');
      return;
    }

    const finalVenueType = card.venueType ?? this.customDescription.trim();

    // Track venue type selection completion
    this.analytics.trackVenueTypeSelection(card.categoryType, finalVenueType);

    // Track signup step completion
    this.funnelTracking.trackStepCompleted('venue_type', {
      venue_type: card.categoryType,
      venue_subtype: finalVenueType,
      is_custom: card.venueType === null,
      nonprofit_status: this.nonprofitStatus || null,
      monthly_order_volume: this.monthlyOrderVolume || null
    });

    // Set user properties
    this.analytics.setUserProperties({
      venue_type: card.categoryType,
      venue_subtype: finalVenueType,
      signup_step: 'venue_selected'
    });

    // Save to session storage
    this.sessionStorageService.setItem(LocalStorageKey.VENUE_CATEGORY_TYPE, card.categoryType);
    this.sessionStorageService.setItem(LocalStorageKey.VENUE_TYPE, finalVenueType);
    if (this.nonprofitStatus) {
      this.sessionStorageService.setItem(LocalStorageKey.NONPROFIT_STATUS, this.nonprofitStatus);
    }
    if (this.monthlyOrderVolume) {
      this.sessionStorageService.setItem(LocalStorageKey.MONTHLY_ORDER_VOLUME, this.monthlyOrderVolume);
    }

    this.sessionStorageService.setItem(LocalStorageKey.VENUE, 'complete');

    this.router.navigate(['/choosePlan']);
  }
}
